import React, { useMemo } from 'react';
import { openForm, openQuickActionProcessingModal } from '../../../redux/actions/modals.actions';
import { useDispatch, useSelector } from 'react-redux';
import { ACCESS_GRANT_AUTHENTICATION_CREDENTIALS_FORM } from '../../AccessGrantAuthenticationCredentials';
import { AccessGrantPageInfo } from '../../../pages/AccessGrant/@types/GetAccessGrantPageInfo';
import { AccessGrantState } from '../../../__generated__/graphql';
import { ActionButton } from './ActionButton';
import { ActionButtonSkeleton } from './ActionButtonSkeleton';
import { KEY_DISPENSE_FORM } from './RemoteKeyDispensingModal';
import { List } from '../styles/ActionsList.styles';
import { QuickActionButtonsList } from '../@types/QuickActionBarTypes';
import { RootState } from '../../../redux/reducers';
import { getActionButtonState } from '../util/actionButtonStateMachine';
import { isEmpty } from 'lodash';
import useCheckInLock from '../../../pages/AccessGrant/hooks/useCheckInLock';
import { useConfirmBeforeProcessingAction } from '../hooks/confirmBeforeExecutingAction';
import { usePerformCheckInOut } from '../hooks/usePerformCheckInOut';
import { useRepollReservation } from '../hooks/useRepollReservation';

export const ActionsList: React.FC = () => {
  const dashboardPreferences = useSelector((state: RootState) => state.dashboardPreferences);
  const accessGrant = useSelector((state: RootState) => state.accessGrant.pageInfo);
  const dispatch = useDispatch();
  const { updateCheckInLockStatus, isCheckInEnabled } = useCheckInLock(
    accessGrant || ({} as AccessGrantPageInfo)
  );
  const { performCheckIn, performCheckOut } = usePerformCheckInOut(accessGrant?.id || '');
  const { repollReservation } = useRepollReservation({
    accessGrantId: accessGrant?.id || '',
    confirmationCode: accessGrant?.confirmationCode || '',
    locationId: accessGrant?.location.id || '',
    tenantId: accessGrant?.tenantId || '',
  });
  const { confirmBeforeProcessingAction } = useConfirmBeforeProcessingAction();

  const quickActionButtonsList = useMemo((): QuickActionButtonsList => {
    const list: QuickActionButtonsList = [
      {
        action: (): void => {
          confirmBeforeProcessingAction({
            action: performCheckIn,
            ctaType: 'checkIn',
          });
        },
        ctaType: 'checkIn',
        text: 'Check-In',
      },
      {
        action: (): void => {
          confirmBeforeProcessingAction({
            action: performCheckOut,
            ctaType: 'checkOut',
          });
        },
        ctaType: 'checkOut',
        text: 'Check-Out',
      },
      {
        action: (): void => {
          confirmBeforeProcessingAction({
            action: updateCheckInLockStatus,
            ctaType: 'blockCheckIn',
          });
        },
        ctaType: 'blockCheckIn',
        text: `${isCheckInEnabled ? 'Block' : 'Unblock'} Check-In`,
      },
      {
        action: (): void => {
          if (accessGrant?.id) {
            dispatch(openForm(ACCESS_GRANT_AUTHENTICATION_CREDENTIALS_FORM, accessGrant.id));
          }
        },
        ctaType: 'getAutenticationCredentials',
        text: 'Authentication Credentials',
      },
      {
        action: (): void => {
          dispatch(openForm(KEY_DISPENSE_FORM));
        },
        ctaType: 'remotePrintKeyCard',
        text: 'Dispense Key',
      },
      {
        action: (): void => {
          dispatch(
            openQuickActionProcessingModal({
              action: repollReservation,
              ctaType: 'repollReservation',
            })
          );
        },
        ctaType: 'repollReservation',
        text: 'Re-Poll',
        conditionToDisplay: accessGrant?.location.supportsPolling,
      },
    ];

    return list;
  }, [isCheckInEnabled]);

  const enabledQuickActions = useMemo((): QuickActionButtonsList => {
    const preferencesActionsList = Object.entries(
      dashboardPreferences?.quickActionBar?.actions || {}
    );

    return quickActionButtonsList.filter(({ ctaType, conditionToDisplay }) => {
      const conditionToDisplayIsSatisfied =
        typeof conditionToDisplay === 'boolean' ? conditionToDisplay : true;

      const ctaTypeIsDisabledInThePreferences = preferencesActionsList.find(
        ([ctaTypeKey, ctaIsActive]) => {
          return ctaType === ctaTypeKey && ctaIsActive === false;
        }
      );

      return isEmpty(ctaTypeIsDisabledInThePreferences) && conditionToDisplayIsSatisfied;
    });
  }, [dashboardPreferences, quickActionButtonsList]);

  return (
    <List>
      {enabledQuickActions?.length > 0 ? (
        enabledQuickActions.map(({ action, ctaType, text }, index) => {
          const agState = accessGrant?.state || AccessGrantState.PreCheckIn;
          const isButtonEnabled = getActionButtonState(ctaType, agState);
          const displayTopSeparatorBar = index % 2 === 0; // Display separator every 2 items

          return (
            <ActionButton
              action={action}
              ctaType={ctaType}
              text={text}
              key={`${index}_${text}`}
              disabled={!isButtonEnabled}
              displayTopSeparatorBar={displayTopSeparatorBar}
            />
          );
        })
      ) : (
        <ActionButtonSkeleton />
      )}
    </List>
  );
};
